import { defineStore } from 'pinia';
import Api, { ToReturn } from '~utils/Api';
import { useMainStore } from '~store/index';
import {
  CalendarEvent, CalendarReply, calendarFailure, makeCalendarRequest,
  readCalendarReply, stringifySqlJson,
} from '~utils/calendarProtocol';

const eventNames: Record<CalendarEvent, string> = {
  snapshot: process.env.VUE_APP_CALENDAR_SNAPSHOT_EVENT || 'calendar_snapshot',
  command: process.env.VUE_APP_CALENDAR_COMMAND_EVENT || 'calendar_command',
  preview: process.env.VUE_APP_CALENDAR_PREVIEW_EVENT || 'calendar_preview',
};

export const useCalendarStore = defineStore('calendar', {
  state: () => ({ idPatient: 0 }),
  actions: {
    setPatient(idPatient: number) { this.idPatient = idPatient; },

    async sendId(): Promise<ToReturn | null> {
      if (!this.idPatient) return null;
      return Api.post({
        ...useMainStore().getConstantParams,
        Name_event: 'send_id',
        Name_module: 'module_calendar',
        json_param: stringifySqlJson({ patient_id: this.idPatient }),
      });
    },

    async receiveId(): Promise<ToReturn | null> {
      return Api.get({
        ...useMainStore().getConstantParams,
        Name_event: 'receive_id',
        Name_module: 'module_calendar',
      });
    },

    async requestCalendar(
      event: CalendarEvent,
      payload: unknown,
      requestId: string,
    ): Promise<CalendarReply> {
      const auth = useMainStore().getConstantParams;
      if (!auth.TK || !auth.IMEI || !auth.Name_app || !auth.id_login) {
        return calendarFailure('NO_CONTEXT', 'Нет данных авторизации от основной страницы.');
      }
      const params = makeCalendarRequest(event, payload, requestId, auth, eventNames);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 30000);
      try {
        // Existing helper's form-body mode avoids URL length limits for schedules.
        const response = event === 'snapshot'
          ? await Api.get(params, controller.signal)
          : await Api.post(params, process.env.VUE_APP_CALENDAR_POST_MODE !== 'query', controller.signal);
        if (controller.signal.aborted) {
          return calendarFailure('TIMEOUT', event === 'command'
            ? 'Сервер не ответил вовремя. Обновите календарь: результат операции пока неизвестен.'
            : 'Сервер не ответил вовремя. Повторите загрузку.');
        }
        return readCalendarReply(response);
      } catch {
        return calendarFailure('REQUEST_FAILED', 'Не удалось получить ответ сервера. Обновите календарь.');
      } finally {
        clearTimeout(timer);
      }
    },
  },
});
